import { supabase } from '@/integrations/supabase/client';
import { VoiceAssistantTool, DEFAULT_VOICE_TOOLS, OpenAITool } from '@/types/voiceAssistantTools';

export class VoiceAssistantToolsService {
  
  static async getToolsForBrand(brandId: string): Promise<VoiceAssistantTool[]> {
    const { data, error } = await supabase
      .from('os_voice_assistant_tools')
      .select('*')
      .eq('brand_id', brandId)
      .order('priority', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching voice assistant tools:', error);
      throw error;
    }

    return (data || []) as VoiceAssistantTool[];
  }

  static async getActiveToolsForBrand(brandId: string): Promise<VoiceAssistantTool[]> {
    const { data, error } = await supabase
      .from('os_voice_assistant_tools')
      .select('*')
      .eq('brand_id', brandId)
      .eq('is_active', true)
      .order('priority', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching active voice assistant tools:', error);
      throw error;
    }

    return (data || []) as VoiceAssistantTool[];
  }

  static async createTool(tool: Omit<VoiceAssistantTool, 'id' | 'created_at' | 'updated_at'>): Promise<VoiceAssistantTool> {
    const { data, error } = await supabase
      .from('os_voice_assistant_tools')
      .insert(tool)
      .select()
      .single();

    if (error) {
      console.error('Error creating voice assistant tool:', error);
      throw error;
    }

    return data as VoiceAssistantTool;
  }

  static async updateTool(id: string, updates: Partial<VoiceAssistantTool>): Promise<VoiceAssistantTool> {
    const { data, error } = await supabase
      .from('os_voice_assistant_tools')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating voice assistant tool:', error);
      throw error;
    }

    return data as VoiceAssistantTool;
  }

  static async deleteTool(id: string): Promise<void> {
    const { error } = await supabase
      .from('os_voice_assistant_tools')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting voice assistant tool:', error);
      throw error;
    }
  }

  static async toggleTool(id: string, isActive: boolean): Promise<VoiceAssistantTool> {
    return this.updateTool(id, { is_active: isActive });
  }

  static async syncDefaultTools(brandId: string): Promise<{ created: number; skipped: number }> {
    const existingTools = await this.getToolsForBrand(brandId);
    const existingNames = new Set(existingTools.map(t => t.name));

    let created = 0;
    let skipped = 0;

    for (const defaultTool of DEFAULT_VOICE_TOOLS) {
      if (existingNames.has(defaultTool.name)) {
        skipped++;
        continue;
      }

      await this.createTool({
        ...defaultTool,
        brand_id: brandId
      });
      created++;
    }

    return { created, skipped };
  }

  static convertToOpenAITools(tools: VoiceAssistantTool[]): OpenAITool[] {
    return tools.map(tool => ({
      type: "function" as const,
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters as Record<string, unknown>
    }));
  }

  static generateSystemInstructions(tools: VoiceAssistantTool[]): string {
    const groupedTools = tools.reduce((acc, tool) => {
      if (!acc[tool.category]) {
        acc[tool.category] = [];
      }
      acc[tool.category].push(tool);
      return acc;
    }, {} as Record<string, VoiceAssistantTool[]>);

    const categoryNames: Record<string, string> = {
      business_operations: "BUSINESS OPERATIONS",
      seo_content: "SEO & CONTENT CREATION",
      analytics: "ANALYTICS",
      communication: "COMMUNICATION",
      general: "GENERAL"
    };

    let instructions = `You are a helpful AI business assistant that can access real business data and help with tasks. You have access to the following functions to help users:\n\n`;

    for (const [category, categoryTools] of Object.entries(groupedTools)) {
      const categoryName = categoryNames[category] || category.toUpperCase();
      instructions += `${categoryName}:\n`;
      
      for (const tool of categoryTools) {
        instructions += `- ${tool.name}: ${tool.description}\n`;
      }
      instructions += '\n';
    }

    instructions += `Always be helpful and provide clear, actionable insights based on the data you retrieve.\n\nCurrent brand context: You are assisting with business operations and various tasks based on the available tools.`;

    return instructions;
  }
}
